import { Box, Chip, Stack, Typography } from "@mui/material";
import type { PersonVote } from "../../api/votes/votes";

/** A run of tokens that either survived from the suggestion or is new. */
type Segment = { text: string; added: boolean };

/**
 * Split into words AND the whitespace between them, so re-joining the tokens
 * reproduces the original text exactly — including the blank lines that carry
 * a pitch's paragraph breaks.
 */
const tokenize = (text: string): string[] => text.split(/(\s+)/).filter(Boolean);

/**
 * Longest common subsequence over word tokens.
 *
 * The suggestion and the sent text are near-identical in the common case (the
 * recruiter tweaks an opener and a sign-off), so the table stays small and a
 * plain O(n*m) fill is far simpler than anything smarter.
 */
const commonSubsequence = (before: string[], after: string[]): boolean[] => {
	const rows = before.length;
	const cols = after.length;
	const table: number[][] = Array.from({ length: rows + 1 }, () =>
		new Array<number>(cols + 1).fill(0)
	);
	for (let i = rows - 1; i >= 0; i--) {
		for (let j = cols - 1; j >= 0; j--) {
			table[i][j] =
				before[i] === after[j]
					? table[i + 1][j + 1] + 1
					: Math.max(table[i + 1][j], table[i][j + 1]);
		}
	}
	// Walk the table forward, marking which tokens of `after` are shared.
	const kept = new Array<boolean>(cols).fill(false);
	let i = 0;
	let j = 0;
	while (i < rows && j < cols) {
		if (before[i] === after[j]) {
			kept[j] = true;
			i++;
			j++;
		} else if (table[i + 1][j] >= table[i][j + 1]) {
			i++;
		} else {
			j++;
		}
	}
	return kept;
};

/** The sent text split into unchanged and recruiter-added runs. */
export const diffSegments = (suggested: string, sent: string): Segment[] => {
	const before = tokenize(suggested);
	const after = tokenize(sent);
	const kept = commonSubsequence(before, after);

	const segments: Segment[] = [];
	for (let index = 0; index < after.length; index++) {
		const added = !kept[index];
		const last = segments[segments.length - 1];
		if (last && last.added === added) {
			last.text += after[index];
		} else {
			segments.push({ text: after[index], added });
		}
	}
	return segments;
};

interface PitchDraftDiffProps {
	vote: PersonVote;
}

/**
 * What the draft-pitch dialog offered versus what actually went out, with the
 * recruiter's additions in bold.
 *
 * Only rendered when a suggestion was stored — the Manual tab suggests nothing,
 * and votes cast before the field existed have none either.
 */
export const PitchDraftDiff = ({ vote }: PitchDraftDiffProps) => {
	const sent = vote.pitch_text;
	const suggested = vote.pitch_suggested_text;
	if (!sent) return null;

	const segments = suggested ? diffSegments(suggested, sent) : null;

	return (
		<Box sx={{ mt: 2 }} data-component="pitch-draft-diff">
			<Stack
				direction="row"
				spacing={1}
				alignItems="center"
				sx={{ mb: 1 }}
				data-component="pitch-draft-diff-meta"
			>
				<Typography variant="subtitle2">Approved pitch</Typography>
				{vote.text_origin && (
					<Chip
						size="small"
						label={vote.text_origin}
						data-component="pitch-draft-origin-chip"
					/>
				)}
				{vote.llm_prompt_template_name && (
					<Chip
						size="small"
						variant="outlined"
						label={vote.llm_prompt_template_name}
						data-component="pitch-draft-prompt-chip"
					/>
				)}
				<Chip
					size="small"
					color={vote.is_text_edited ? "warning" : "success"}
					label={vote.is_text_edited ? "edited" : "sent verbatim"}
					data-component="pitch-draft-edited-chip"
				/>
				{vote.pitch_channel && (
					<Chip
						size="small"
						variant="outlined"
						label={`pinned: ${vote.pitch_channel}`}
						data-component="pitch-draft-channel-chip"
					/>
				)}
			</Stack>

			{vote.pitch_subject && (
				<Typography
					variant="body2"
					sx={{ mb: 1 }}
					data-component="pitch-draft-subject"
				>
					<strong>Subject:</strong> {vote.pitch_subject}
				</Typography>
			)}

			{suggested && (
				<>
					<Typography variant="caption" color="text.secondary">
						Suggested
					</Typography>
					<Box
						sx={{
							p: 1,
							mb: 2,
							bgcolor: "action.hover",
							borderRadius: 1,
							fontSize: 13,
							whiteSpace: "pre-wrap",
						}}
						data-component="pitch-draft-suggested"
					>
						{suggested}
					</Box>
				</>
			)}

			<Typography variant="caption" color="text.secondary">
				{segments ? "Sent — recruiter's changes in bold" : "Sent"}
			</Typography>
			<Box
				sx={{
					p: 1,
					bgcolor: "action.hover",
					borderRadius: 1,
					fontSize: 13,
					whiteSpace: "pre-wrap",
				}}
				data-component="pitch-draft-sent"
			>
				{segments
					? segments.map((segment, index) =>
							segment.added ? (
								<Box
									// Segments are positional; nothing stable to key on.
									key={index}
									component="strong"
									sx={{ color: "warning.main" }}
								>
									{segment.text}
								</Box>
							) : (
								<span key={index}>{segment.text}</span>
							)
						)
					: sent}
			</Box>
		</Box>
	);
};
